/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './StepSwitcherSection.module.scss';

import type { StepSwitcherSectionProps } from './StepSwitcherSection.types';

export const StepSwitcherSection = ({ eyebrow, title, description, steps, className }: StepSwitcherSectionProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const zoneRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [active, setActive] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  useEffect(() => {
    const items = zoneRefs.current.filter((item): item is HTMLDivElement => item !== null);

    if (items.length === 0) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const index = items.indexOf(entry.target as HTMLDivElement);

          if (index !== -1) {
            setActive(index);
          }
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
    );

    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [steps]);

  const selectTab = (index: number) => {
    setActive(index);
    zoneRefs.current[index]?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
  };

  return (
    <section className={cn(styles.root, className)}>
      <div className={styles.inner}>
        <div className={styles.track} ref={trackRef}>
          <div className={styles.zones}>
            {steps.map((step, i) => (
              <div
                key={step.key}
                ref={(el) => {
                  zoneRefs.current[i] = el;
                }}
                className={styles.zone}
              />
            ))}
          </div>

          <div className={styles.sticky}>
            <div className={styles.head}>
              <TitleBlock extra={eyebrow} title={title} description={description} className={styles.titleBlock} />
            </div>

            <div className={styles.switcher}>
              <div className={styles.tabs} role="tablist" aria-label={typeof title === 'string' ? title : undefined}>
                {steps.map((step, i) => {
                  const on = i === active;
                  const dstep = i < active;

                  return (
                    <button
                      key={step.key}
                      type="button"
                      role="tab"
                      aria-selected={on}
                      className={cn(styles.tab, on && styles.tabActive, dstep && styles.tabDone)}
                      onClick={() => selectTab(i)}
                    >
                      <i className={styles.tabIndex}>{step.index}</i>
                      <span className={styles.tabCopy}>
                        <b>{step.tabTitle}</b>
                        <span>{step.tabSub}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className={styles.stage}>
                {steps.map((step, i) => {
                  const on = i === active;

                  return (
                    <div
                      key={step.key}
                      className={cn(styles.panel, on && styles.panelActive)}
                      role="tabpanel"
                      aria-hidden={!on}
                    >
                      <div className={styles.copy}>
                        <h3 className={styles.panelTitle}>{step.title}</h3>
                        {step.body}
                      </div>

                      <div className={styles.card}>{step.card}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
